import { useEffect, useState } from "react";
import { Check, Copy, ImagePlus, Loader2, Save, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import PaymentPolicyField from "@/components/admin/PaymentPolicyField";
import PaymentConnectionPanel from "@/components/admin/PaymentConnectionPanel";
import { BUSINESS_TYPE_LABELS } from "@/lib/terminology";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function TenantProfileTab({ tenant, reload }) {
  const [form, setForm] = useState({ name: "", phone: "", slug: "", business_type: "BARBERSHOP", theme_id: "PREMIUM_GOLD" });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [protection, setProtection] = useState({ no_show_protection_enabled: false, payment_provider: "PAGBANK", deposit_type: "FIXED", deposit_value: 10, payment_hold_minutes: 10, late_tolerance_minutes: 10, late_release_mode: "MANUAL" });
  const [savingProtection, setSavingProtection] = useState(false);

  useEffect(() => {
    setForm({ name: tenant?.name || "", phone: tenant?.phone || "", slug: tenant?.slug || "", business_type: tenant?.business_type || "BARBERSHOP", theme_id: tenant?.theme_id || "PREMIUM_GOLD" });
    if (tenant?.no_show_protection) setProtection({ ...tenant.no_show_protection, payment_policy: { mode: "NONE", deposit_type: "FIXED", deposit_value: 10, ...tenant.no_show_protection.payment_policy } });
  }, [tenant]);

  const saveProtection = async (event) => {
    event.preventDefault();
    if (["DEPOSIT", "FULL_PREPAY"].includes(protection.payment_policy?.mode) &&
        !(tenant?.payment_connection?.can_activate ?? protection.provider_available)) {
      toast.error("Conecte um meio de pagamento para ativar cobranças antecipadas.");
      return;
    }
    setSavingProtection(true);
    try {
      await api.put("/admin/tenant/no-show-protection", protection);
      await reload();
      toast.success("Política de pagamento atualizada.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Não foi possível salvar a política.");
    } finally { setSavingProtection(false); }
  };

  // Use the saved tenant slug so a failed edit can never expose a non-existent URL.
  const publicLink = tenant?.slug ? `${window.location.origin}/agendar/${tenant.slug}` : "";

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/tenant", form);
      await reload();
      toast.success("Perfil atualizado.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Não foi possível atualizar o perfil.");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!publicLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicLink);
      } else {
        const input = document.createElement("textarea");
        input.value = publicLink;
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/admin/tenant/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
      toast.success("Logo atualizada.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Não foi possível atualizar a logo.");
    }
  };

  const remove = async () => {
    try {
      await api.delete("/admin/tenant/logo");
      await reload();
      toast.success("Logo removida. O branding padrão voltou a ser exibido.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Não foi possível remover a logo.");
    }
  };

  return (
    <section className="fade-in max-w-2xl">
      <h1 className="font-display text-3xl gold-text">Perfil do estabelecimento</h1>
      <p className="mt-2 text-sm text-stone-400 font-sans2">Gerencie as informações exibidas no painel e no agendamento público.</p>
      <form onSubmit={saveProfile} className="card-surface mt-6 p-5 space-y-4">
        <h2 className="font-sans2 font-semibold text-amber-50">Dados públicos</h2>
        <Field label="Nome do estabelecimento">
          <input data-testid="tenant-name-input" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className={inputCls} />
        </Field>
        <Field label="Telefone / WhatsApp">
          <input data-testid="tenant-phone-input" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} className={inputCls} placeholder="Ex.: (11) 99999-9999" />
        </Field>
        <Field label="Slug">
          <input data-testid="tenant-slug-input" required value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} className={inputCls} placeholder="meu-estabelecimento" />
        </Field>
        <Field label="Tipo de negócio"><select value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} className={inputCls}>{Object.entries(BUSINESS_TYPE_LABELS).map(([id,label]) => <option key={id} value={id}>{label}</option>)}</select></Field>
        <Field label="Tema"><select value={form.theme_id} onChange={(e) => setForm({ ...form, theme_id: e.target.value })} className={inputCls}>{[["PREMIUM_GOLD","Dourado premium"],["ROSE_BEAUTY","Rosa beauty"],["CLEAN_NUDE","Nude clean"],["MINIMAL_LIGHT","Claro minimalista"],["NATURAL","Natural"]].map(([id,label]) => <option key={id} value={id}>{label}</option>)}</select></Field>
        <p className="text-xs text-stone-500">Use letras, números e hífens. Ao salvar, o link anterior deixa de funcionar.</p>
        <button data-testid="save-tenant-profile" disabled={saving} className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0B0E] px-4 py-2.5 rounded-lg font-sans2 font-semibold text-sm disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar perfil
        </button>
      </form>
      <PaymentConnectionPanel connection={tenant?.payment_connection} reload={reload} />
      <form onSubmit={saveProtection} className="card-surface mt-6 p-5 space-y-4" data-testid="no-show-settings">
        <h2 className="font-sans2 font-semibold text-amber-50">Política de pagamento</h2>
        <p className="text-sm text-stone-400">Escolha se a confirmação exige pagamento antecipado. O sinal vira crédito no serviço. Cancelamentos do estabelecimento são tratados separadamente.</p>
        <PaymentPolicyField allowInherit={false} value={protection.payment_policy} onChange={(payment_policy) => setProtection({ ...protection, payment_policy, no_show_protection_enabled: payment_policy.mode !== "NONE" })} />
        {!tenant?.payment_connection || tenant.payment_connection.source === "LEGACY_GLOBAL" ? <Field label="Provedor"><select data-testid="no-show-provider" value={protection.payment_provider} onChange={(e) => setProtection({ ...protection, payment_provider: e.target.value })} className={inputCls}>
          <option value="PAGBANK">PagBank</option>{process.env.NODE_ENV !== "production" && <option value="FAKE">Fake (teste local)</option>}
        </select></Field> : <p className="text-sm text-stone-300">Provedor: PagBank</p>}
        <Field label="Prazo para pagamento (minutos)"><input data-testid="no-show-hold" type="number" min="2" max="60" value={protection.payment_hold_minutes} onChange={(e) => setProtection({ ...protection, payment_hold_minutes: Number(e.target.value) })} className={inputCls} /></Field>
        <Field label="Tolerância de atraso (minutos)"><input data-testid="no-show-tolerance" type="number" min="0" max="120" value={protection.late_tolerance_minutes} onChange={(e) => setProtection({ ...protection, late_tolerance_minutes: Number(e.target.value) })} className={inputCls} /></Field>
        <p className="text-xs text-stone-500">Após esse período, o horário poderá ser liberado para atendimento presencial. A liberação é manual.</p>
        {!protection.provider_available && <p className="text-xs text-amber-400">Conecte um meio de pagamento para ativar cobranças antecipadas.</p>}
        <button data-testid="save-no-show-settings" disabled={savingProtection} className="bg-[#D4AF37] text-[#0A0B0E] px-4 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50">{savingProtection ? "Salvando…" : "Salvar proteção"}</button>
      </form>
      <section className="card-surface mt-6 p-5">
        <h2 className="font-sans2 font-semibold text-amber-50">Seu link de agendamento</h2>
        <p data-testid="public-booking-link" className="mt-3 break-all rounded-lg border border-[#282C37] bg-[#0A0B0E] px-3 py-2 font-mono2 text-sm text-[#D4AF37]">{publicLink || "Informe um slug para gerar o link."}</p>
        <button data-testid="copy-public-booking-link" type="button" disabled={!publicLink} onClick={copyLink} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#D4AF37]/40 px-3 py-2 text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Link copiado" : "Copiar link"}
        </button>
      </section>
      <div className="card-surface mt-6 p-5">
        <h2 className="font-sans2 font-semibold text-amber-50">Logo</h2>
        {tenant?.logo_url ? (
          <div className="mt-4 rounded-lg border border-[#282C37] bg-[#0A0B0E] p-4">
            <img data-testid="tenant-current-logo" src={`${BACKEND_URL}${tenant.logo_url}`} alt={`Logo de ${tenant.name}`} className="h-24 w-full object-contain" />
          </div>
        ) : (
          <p data-testid="tenant-logo-fallback" className="mt-4 text-sm text-stone-400">Nenhuma logo cadastrada. O branding padrão está em uso.</p>
        )}
        <div className="mt-4 flex flex-wrap gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[#D4AF37]/40 px-3 py-2 text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10">
            <ImagePlus className="w-4 h-4" />{tenant?.logo_url ? "Substituir logo" : "Enviar logo"}
            <input data-testid="tenant-logo-input" type="file" className="hidden" accept="image/png,image/jpeg,image/webp" onChange={(event) => upload(event.target.files?.[0])} />
          </label>
          {tenant?.logo_url && <button data-testid="remove-tenant-logo" onClick={remove} className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" />Remover logo</button>}
        </div>
        <p className="mt-3 text-xs text-stone-500">PNG, JPG/JPEG ou WEBP, até 2 MB.</p>
      </div>
    </section>
  );
}

const inputCls = "w-full bg-[#0A0B0E] border border-[#282C37] rounded-lg px-3 py-2 text-amber-50 focus:border-[#D4AF37] focus:outline-none font-sans2 text-sm";

function Field({ label, children }) {
  return <label className="block text-sm text-stone-300 font-sans2">{label}<span className="mt-1.5 block">{children}</span></label>;
}
